<template>
    <Transition name="dc-drawer">
        <div v-if="ds.state.customizeOpen" class="dc-overlay" @click.self="ds.closeCustomize()">
            <aside class="dc-drawer" @click.stop>
                <header class="dc-header">
                    <button v-if="mode !== 'list'" class="dc-back-btn" @click="back" title="Geri">
                        <i class="bi bi-chevron-left"></i>
                    </button>
                    <div v-else class="dc-header-title">
                        <i class="bi bi-grid-1x2-fill"></i>
                    </div>
                    <span class="dc-header-text">{{ headerTitle }}</span>
                    <button class="dc-close-btn" @click="ds.closeCustomize()" :title="$t('common.dashboard.close')">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </header>

                <div class="dc-modes" :class="'dc-modes--' + mode">
                    <Transition :name="slideTransitionName">
                    <div v-if="mode === 'list'" class="dc-mode" key="list">
                        <section class="dc-section">
                            <h3 class="dc-section-title">{{ $t('common.dashboard.appearance') }}</h3>
                            <label class="dc-row dc-row--clickable" @click.prevent="ds.toggleGlass()">
                                <span class="dc-row-icon" style="background: rgba(59, 130, 246, 0.12); color: #3B82F6">
                                    <i class="bi bi-droplet-half"></i>
                                </span>
                                <span class="dc-row-info">
                                    <span class="dc-row-title">{{ $t('common.dashboard.glassMode') }}</span>
                                    <span class="dc-row-sub">{{ $t('common.dashboard.glassModeDesc') }}</span>
                                </span>
                                <span class="dc-toggle" :class="{ 'dc-toggle--on': ds.state.glassMode }">
                                    <span class="dc-toggle-thumb"></span>
                                </span>
                            </label>
                        </section>

                        <section class="dc-section">
                            <div class="dc-section-head">
                                <h3 class="dc-section-title">{{ $t('common.dashboard.activeWidgets') }}</h3>
                                <div class="dc-section-actions">
                                    <button class="dc-mini-btn" @click="ds.showAll()" :disabled="!hasHidden">{{ $t('common.dashboard.showAll') }}</button>
                                    <button class="dc-mini-btn" @click="ds.hideAll()" :disabled="hasNoneVisible">{{ $t('common.dashboard.hideAll') }}</button>
                                </div>
                            </div>
                            <ul v-if="ds.state.instances.length" class="dc-instance-list">
                                <li v-for="(inst, idx) in ds.state.instances" :key="inst.id"
                                    class="dc-row"
                                    :class="{
                                        'dc-row--off': ds.isHidden(inst.id),
                                        'dc-row--dragging': draggingId === inst.id,
                                        'dc-row--drag-over': dragOverIdx === idx && draggingId && draggingId !== inst.id
                                    }"
                                    draggable="true"
                                    @dragstart="onRowDragStart($event, inst, idx)"
                                    @dragend="onRowDragEnd"
                                    @dragover.prevent="onRowDragOver($event, idx)"
                                    @drop="onRowDrop($event, idx)">
                                    <span class="dc-row-grip" title="Sürükle"
                                          @touchstart.stop.prevent="onRowTouchStart($event, inst, idx)">
                                        <i class="bi bi-grip-vertical"></i>
                                    </span>
                                    <span class="dc-row-icon" :style="iconStyle(inst)">
                                        <i :class="['bi', templateOf(inst).icon]"></i>
                                    </span>
                                    <span class="dc-row-info" @click="onEdit(inst)" style="cursor: pointer;">
                                        <span class="dc-row-title">{{ instanceTitle(inst) }}</span>
                                        <span v-if="instanceSubtitle(inst)" class="dc-row-sub">{{ instanceSubtitle(inst) }}</span>
                                    </span>
                                    <button class="dc-icon-btn" :title="ds.isHidden(inst.id) ? 'Göster' : 'Gizle'" @click="ds.toggleHidden(inst.id)">
                                        <i :class="['bi', ds.isHidden(inst.id) ? 'bi-eye-slash' : 'bi-eye']"></i>
                                    </button>
                                    <button class="dc-icon-btn dc-icon-btn--danger" title="Sil" @click="onDelete(inst)">
                                        <i class="bi bi-trash"></i>
                                    </button>
                                </li>
                            </ul>
                            <div v-else class="dc-empty">
                                <i class="bi bi-grid"></i>
                                <span>Henüz widget yok</span>
                            </div>
                        </section>
                    </div>

                    <div v-else-if="mode === 'add'" class="dc-mode" key="add">
                        <div class="dc-template-grid">
                            <button v-for="tpl in availableTemplates" :key="tpl.id"
                                    class="dc-template-card"
                                    :disabled="!ds.canAddTemplate(tpl.id)"
                                    @click="onPickTemplate(tpl)">
                                <span class="dc-template-icon" :style="{ background: tpl.accentBg, color: tpl.accent }">
                                    <i :class="['bi', tpl.icon]"></i>
                                </span>
                                <span class="dc-template-name">{{ $t(tpl.titleKey) }}</span>
                                <span v-if="tpl.multiInstance" class="dc-template-dot dc-template-dot--multi" title="Birden fazla eklenebilir"></span>
                                <span v-else-if="!ds.canAddTemplate(tpl.id)" class="dc-template-dot dc-template-dot--added" title="Eklendi"></span>
                            </button>
                        </div>
                    </div>

                    <div v-else-if="mode === 'form'" class="dc-mode" key="form">
                        <div class="dc-form">
                            <div class="dc-form-header">
                                <span class="dc-form-icon" :style="{ background: selectedTemplate.accentBg, color: selectedTemplate.accent }">
                                    <i :class="['bi', selectedTemplate.icon]"></i>
                                </span>
                                <span class="dc-form-name">{{ $t(selectedTemplate.titleKey) }}</span>
                            </div>

                            <div v-if="!hasFields" class="dc-form-empty">
                                Bu widget için yapılandırma gerekmez.
                            </div>

                            <div v-for="field in fields" :key="field.key" class="dc-form-field">
                                <label class="dc-form-label">{{ $t(field.label) }}</label>
                                <el-select v-if="field.type === 'select'" v-model="formConfig[field.key]" size="default" class="dc-form-input">
                                    <el-option v-for="opt in field.options" :key="opt.value" :label="$t(opt.label)" :value="opt.value" />
                                </el-select>
                                <el-input-number v-else-if="field.type === 'number'"
                                                 v-model="formConfig[field.key]"
                                                 :min="field.min" :max="field.max" :step="field.step || 1"
                                                 step-strictly
                                                 size="default" class="dc-form-input" />
                                <el-select v-else-if="field.type === 'cash_register_select'" v-model="formConfig.cash_register_id" size="default" class="dc-form-input" placeholder="Varsayılan">
                                    <el-option :label="'Varsayılan kasa'" :value="null" />
                                    <el-option v-for="r in cashRegisters" :key="r.id" :label="r.name" :value="r.id" />
                                </el-select>
                                <el-select v-else-if="field.type === 'sms_provider_select'" v-model="formConfig.provider" size="default" class="dc-form-input">
                                    <el-option v-for="p in smsProviders" :key="p.code" :label="p.name" :value="p.code" />
                                </el-select>
                                <div v-else-if="field.type === 'weather_location'" class="dc-form-weather">
                                    <div v-if="formConfig.location_label && !weatherSearching" class="dc-form-selected">
                                        <i class="bi bi-geo-alt-fill"></i>
                                        <span>{{ formConfig.location_label }}</span>
                                        <button class="dc-form-selected-clear" @click="weatherSearching = true">Değiştir</button>
                                    </div>
                                    <div v-else class="dc-weather-search">
                                        <input ref="weatherInput" type="text" v-model="weatherQuery" class="dc-form-text" :placeholder="$t('common.weather.search')"
                                               @input="onWeatherInput" @keydown.esc.stop="cancelWeatherSearch" />
                                        <div v-if="weatherResults.length" class="dc-weather-results">
                                            <button v-for="r in weatherResults" :key="r.city_id + '-' + (r.district_id || 0)" type="button"
                                                    class="dc-weather-result"
                                                    @click="onWeatherSelect(r)">
                                                <i class="bi bi-geo-alt"></i>
                                                <span>{{ r.label }}</span>
                                            </button>
                                        </div>
                                        <div v-else-if="weatherQuery.length >= 2 && !weatherLoading && weatherSearched" class="dc-weather-empty">
                                            {{ $t('common.weather.noResults') }}
                                        </div>
                                        <div v-else-if="weatherLoading" class="dc-weather-empty">{{ $t('common.weather.loading') }}</div>
                                    </div>
                                    <small class="dc-form-hint">Boş bırakırsan varsayılan konum gösterilir.</small>
                                </div>
                            </div>

                            <button class="dc-submit-btn" @click="onSubmit">
                                {{ formMode === 'add' ? 'Ekle' : 'Kaydet' }}
                            </button>
                        </div>
                    </div>
                    </Transition>
                </div>

                <footer v-if="mode === 'list'" class="dc-footer">
                    <button class="dc-add-btn" @click="onAddClick">
                        <i class="bi bi-plus-lg"></i>
                        <span>{{ $t('common.dashboard.addWidget') }}</span>
                    </button>
                    <button class="dc-reset-btn" @click="onReset">
                        <i class="bi bi-arrow-counterclockwise"></i>
                        <span>{{ $t('common.dashboard.reset') }}</span>
                    </button>
                </footer>
            </aside>
        </div>
    </Transition>
</template>

<script>
import dashboardService from './dashboardService';
import weatherService from '@/components/weather/weatherService';

export default {
    name: 'DashboardCustomizeDrawer',
    data() {
        return {
            ds: dashboardService,
            mode: 'list',
            slideDir: 'forward',
            formMode: 'add',
            selectedTemplate: null,
            editingInstance: null,
            formConfig: {},
            weatherQuery: '',
            weatherResults: [],
            weatherLoading: false,
            weatherSearched: false,
            weatherSearching: false,
            draggingId: null,
            dragOverIdx: null,
        };
    },
    watch: {
        'ds.state.customizeOpen'(open) {
            if (open) {
                this._prevBodyOverflow = document.body.style.overflow;
                document.body.style.overflow = 'hidden';
                this.slideDir = 'forward';
                this.mode = 'list';
            } else {
                document.body.style.overflow = this._prevBodyOverflow || '';
            }
        },
    },
    created() {
        this._prevBodyOverflow = '';
        this._weatherDebounce = null;
        this._touchDrag = null;
    },
    mounted() {
        document.addEventListener('keydown', this._onKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this._onKeydown);
        if (this.ds.state.customizeOpen) {
            document.body.style.overflow = this._prevBodyOverflow || '';
        }
    },
    computed: {
        templates() {
            return this.ds.getTemplates();
        },
        slideTransitionName() {
            return 'dc-slide-' + this.slideDir;
        },
        availableTemplates() {
            return this.templates;
        },
        hasHidden() {
            return this.ds.state.hidden.length > 0;
        },
        hasNoneVisible() {
            return this.ds.getVisibleInstances().length === 0;
        },
        headerTitle() {
            if (this.mode === 'add') return 'Widget Şablonları';
            if (this.mode === 'form') {
                return this.formMode === 'add' ? 'Widget Ekle' : 'Widget Düzenle';
            }
            return this.$t('common.dashboard.customizeTitle');
        },
        fields() {
            return this.selectedTemplate?.configFields || [];
        },
        hasFields() {
            return this.fields.length > 0;
        },
        cashRegisters() {
            const list = this.ds.state.statisticsRaw?.cash_registers;
            return Array.isArray(list) ? list : [];
        },
        smsProviders() {
            try {
                const modules = JSON.parse(localStorage.getItem('company_modules') || '[]');
                return modules
                    .filter(m => m?.kind?.code === 'SMS_PROVIDER' && m?.supported?.widget)
                    .map(m => ({ code: m.code, name: m.name }));
            } catch {
                return [];
            }
        },
    },
    methods: {
        templateOf(instance) {
            return this.templates.find(t => t.id === instance.templateId) || {};
        },
        iconStyle(instance) {
            const tpl = this.templateOf(instance);
            return { background: tpl.accentBg, color: tpl.accent };
        },
        instanceTitle(instance) {
            const tpl = this.templateOf(instance);
            return this.$t(tpl.titleKey);
        },
        instanceSubtitle(instance) {
            const tpl = this.templateOf(instance);
            const cfg = instance.config || {};
            if (tpl.id === 'sms_credit' || tpl.id === 'sms_credit_simple' || tpl.id === 'sms_titles') {
                return this._providerName(cfg.provider);
            }
            if (tpl.id === 'cash_register' && cfg.cash_register_id) {
                const r = this.cashRegisters.find(x => x.id === cfg.cash_register_id);
                return r?.name || `Kasa #${cfg.cash_register_id}`;
            }
            if (tpl.id === 'weather' && cfg.location_label) {
                return cfg.location_label;
            }
            if (tpl.id === 'work_order_recent') {
                const parts = [];
                if (cfg.status_id) {
                    const map = { 1: 'Açık', 2: 'İşlemde', 3: 'Tamamlandı', 4: 'Tedarik Bekleniyor', 5: 'İptal Edildi', 6: 'İade Edildi' };
                    parts.push(map[cfg.status_id]);
                }
                if (cfg.limit) parts.push(`${cfg.limit} kayıt`);
                return parts.join(' · ');
            }
            return '';
        },
        _providerName(code) {
            if (!code) return '';
            const found = this.smsProviders.find(p => p.code === code);
            return found?.name || code;
        },
        back() {
            this.slideDir = 'backward';
            if (this.mode === 'form') {
                this.mode = this.formMode === 'add' ? 'add' : 'list';
            } else if (this.mode === 'add') {
                this.mode = 'list';
            }
        },
        onAddClick() {
            this.slideDir = 'forward';
            this.mode = 'add';
        },
        onPickTemplate(tpl) {
            if (!this.ds.canAddTemplate(tpl.id)) return;
            this.selectedTemplate = tpl;
            this.formMode = 'add';
            this.editingInstance = null;
            this.formConfig = { ...(tpl.defaultConfig || {}) };
            this._resetWeather();
            this.slideDir = 'forward';
            this.mode = 'form';
        },
        onEdit(instance) {
            const tpl = this.templateOf(instance);
            if (!tpl.configFields || !tpl.configFields.length) return;
            this.selectedTemplate = tpl;
            this.formMode = 'edit';
            this.editingInstance = instance;
            this.formConfig = { ...(tpl.defaultConfig || {}), ...instance.config };
            this._resetWeather();
            this.slideDir = 'forward';
            this.mode = 'form';
        },
        _resetWeather() {
            this.weatherQuery = '';
            this.weatherResults = [];
            this.weatherLoading = false;
            this.weatherSearched = false;
            this.weatherSearching = false;
        },
        onWeatherInput() {
            clearTimeout(this._weatherDebounce);
            const q = this.weatherQuery.trim();
            if (q.length < 2) {
                this.weatherResults = [];
                this.weatherSearched = false;
                this.weatherLoading = false;
                return;
            }
            this.weatherLoading = true;
            this._weatherDebounce = setTimeout(async () => {
                try {
                    this.weatherResults = await weatherService.searchCity(q) || [];
                } catch {
                    this.weatherResults = [];
                }
                this.weatherLoading = false;
                this.weatherSearched = true;
            }, 350);
        },
        onWeatherSelect(r) {
            const parts = (r.label || '').split(', ');
            const districtName = parts[0] || '';
            const cityName = parts.length > 1 ? parts[1] : parts[0];
            this.formConfig = {
                ...this.formConfig,
                city_id: r.city_id || null,
                district_id: r.district_id || null,
                location_label: r.district_id ? `${districtName}, ${cityName}` : cityName,
            };
            this.weatherSearching = false;
            this.weatherQuery = '';
            this.weatherResults = [];
        },
        cancelWeatherSearch() {
            this.weatherSearching = false;
            this.weatherQuery = '';
            this.weatherResults = [];
        },
        onSubmit() {
            if (this.formMode === 'add') {
                this.ds.addInstance(this.selectedTemplate.id, this.formConfig);
            } else if (this.editingInstance) {
                this.ds.updateInstanceConfig(this.editingInstance.id, this.formConfig);
            }
            this.ds.loadAll();
            this.slideDir = 'backward';
            this.mode = 'list';
        },
        onDelete(instance) {
            const title = this.instanceTitle(instance);
            this.eventBus.$emit('showConfirmDialog', {
                title: 'Widget Sil',
                message: `"${title}" silinecek. Devam edilsin mi?`,
                confirmText: 'Sil',
                cancelText: this.$t('common.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    this.ds.removeInstance(instance.id);
                },
            });
        },
        onReset() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.dashboard.resetConfirmTitle'),
                message: this.$t('common.dashboard.resetConfirmMessage'),
                confirmText: this.$t('common.dashboard.resetConfirmAction'),
                cancelText: this.$t('common.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    this.ds.reset();
                    this.ds.loadAll();
                },
            });
        },
        _onKeydown(e) {
            if (e.key !== 'Escape' || !this.ds.state.customizeOpen) return;
            if (this.mode !== 'list') {
                this.back();
            } else {
                this.ds.closeCustomize();
            }
        },
        onRowDragStart(e, inst, idx) {
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', inst.id); } catch { /* empty */ }
            this.draggingId = inst.id;
            this._dragFromIdx = idx;
            const row = e.currentTarget;
            this._mouseDrag = {
                scrollHost: this._findScrollParent(row),
                mouseY: e.clientY,
            };
            this._mouseDragMoveHandler = (ev) => {
                if (this._mouseDrag) this._mouseDrag.mouseY = ev.clientY;
            };
            document.addEventListener('dragover', this._mouseDragMoveHandler);
            this._mouseAutoScrollRAF = requestAnimationFrame(() => this._mouseAutoScrollTick());
        },
        onRowDragEnd() {
            this.draggingId = null;
            this.dragOverIdx = null;
            cancelAnimationFrame(this._mouseAutoScrollRAF);
            if (this._mouseDragMoveHandler) {
                document.removeEventListener('dragover', this._mouseDragMoveHandler);
                this._mouseDragMoveHandler = null;
            }
            this._mouseDrag = null;
        },
        onRowDragOver(e, idx) {
            if (!this.draggingId) return;
            e.dataTransfer.dropEffect = 'move';
            if (this.dragOverIdx !== idx) this.dragOverIdx = idx;
        },
        onRowDrop(e, targetIdx) {
            if (!this.draggingId) return;
            this._reorder(this.draggingId, targetIdx);
            this.draggingId = null;
            this.dragOverIdx = null;
        },
        _reorder(instanceId, targetIdx) {
            const ids = this.ds.state.instances.map(i => i.id);
            const fromIdx = ids.indexOf(instanceId);
            if (fromIdx === -1 || fromIdx === targetIdx) return;
            const [item] = ids.splice(fromIdx, 1);
            const insertIdx = fromIdx < targetIdx ? targetIdx - 1 : targetIdx;
            ids.splice(insertIdx, 0, item);
            this.ds.setOrder(ids);
        },
        onRowTouchStart(e, inst, idx) {
            const row = e.currentTarget.closest('.dc-row');
            if (!row) return;
            const list = row.parentElement;
            const scrollHost = this._findScrollParent(row);
            const rect = row.getBoundingClientRect();
            const touch = e.touches[0];
            const ghost = row.cloneNode(true);
            ghost.style.cssText = 'position:fixed;z-index:200000;pointer-events:none;opacity:0.92;'
                + 'width:' + rect.width + 'px;left:' + rect.left + 'px;'
                + 'background:var(--bs-body-bg);border-radius:10px;'
                + 'box-shadow:0 6px 20px rgba(0,0,0,0.22);transition:none;';
            ghost.style.top = rect.top + 'px';
            document.body.appendChild(ghost);
            this._touchDrag = {
                instanceId: inst.id,
                fromIdx: idx,
                ghost,
                list,
                scrollHost,
                offsetY: touch.clientY - rect.top,
                itemHeight: rect.height,
                touchY: touch.clientY,
            };
            this.draggingId = inst.id;
            try { navigator.vibrate(10); } catch { /* empty */ }

            this._touchMoveHandler = (ev) => this._onRowTouchMove(ev);
            this._touchEndHandler = () => this._onRowTouchEnd();
            document.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
            document.addEventListener('touchend', this._touchEndHandler);
            document.addEventListener('touchcancel', this._touchEndHandler);
            this._autoScrollRAF = requestAnimationFrame(() => this._autoScrollTick());
        },
        _onRowTouchMove(e) {
            const g = this._touchDrag;
            if (!g) return;
            e.preventDefault();
            const touch = e.touches[0];
            g.touchY = touch.clientY;
            g.ghost.style.top = (touch.clientY - g.offsetY) + 'px';
            this._recalcDropTarget();
        },
        _recalcDropTarget() {
            const g = this._touchDrag;
            if (!g) return;
            const listRect = g.list.getBoundingClientRect();
            const relativeY = g.touchY - listRect.top;
            const total = this.ds.state.instances.length;
            const targetIdx = Math.max(0, Math.min(Math.floor(relativeY / g.itemHeight), total - 1));
            this.dragOverIdx = targetIdx !== g.fromIdx ? targetIdx : null;
        },
        _autoScrollOnce(scrollHost, y) {
            if (!scrollHost) return false;
            const hostRect = scrollHost.getBoundingClientRect();
            const edge = 50;
            const speed = 6;
            if (y < hostRect.top + edge && scrollHost.scrollTop > 0) {
                scrollHost.scrollTop -= speed;
                return true;
            }
            if (y > hostRect.bottom - edge) {
                scrollHost.scrollTop += speed;
                return true;
            }
            return false;
        },
        _autoScrollTick() {
            const g = this._touchDrag;
            if (!g) return;
            if (this._autoScrollOnce(g.scrollHost, g.touchY)) {
                this._recalcDropTarget();
            }
            this._autoScrollRAF = requestAnimationFrame(() => this._autoScrollTick());
        },
        _mouseAutoScrollTick() {
            const m = this._mouseDrag;
            if (!m) return;
            this._autoScrollOnce(m.scrollHost, m.mouseY);
            this._mouseAutoScrollRAF = requestAnimationFrame(() => this._mouseAutoScrollTick());
        },
        _onRowTouchEnd() {
            cancelAnimationFrame(this._autoScrollRAF);
            document.removeEventListener('touchmove', this._touchMoveHandler);
            document.removeEventListener('touchend', this._touchEndHandler);
            document.removeEventListener('touchcancel', this._touchEndHandler);
            const g = this._touchDrag;
            if (!g) {
                this.draggingId = null;
                this.dragOverIdx = null;
                return;
            }
            g.ghost.remove();
            if (this.dragOverIdx != null) {
                this._reorder(g.instanceId, this.dragOverIdx);
            }
            this._touchDrag = null;
            this.draggingId = null;
            this.dragOverIdx = null;
        },
        _findScrollParent(el) {
            let cur = el?.parentElement;
            while (cur && cur !== document.body) {
                const oy = getComputedStyle(cur).overflowY;
                if (oy === 'auto' || oy === 'scroll') return cur;
                cur = cur.parentElement;
            }
            return null;
        },
    },
};
</script>

<style>
.dc-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    z-index: 1050;
    display: flex;
    justify-content: flex-end;
}

.dc-drawer {
    width: 380px;
    max-width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: #ffffff;
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

html.dark .dc-drawer,
html.midnight .dc-drawer,
html.cream .dc-drawer {
    backdrop-filter: blur(48px) saturate(160%);
    -webkit-backdrop-filter: blur(48px) saturate(160%);
}

html.dark .dc-drawer {
    background: rgba(20, 22, 28, 0.90);
    border-left-color: rgba(255, 255, 255, 0.06);
}
html.midnight .dc-drawer {
    background: rgba(24, 21, 18, 0.90);
    border-left-color: rgba(200, 134, 60, 0.10);
}
html.cream .dc-drawer {
    background: rgba(250, 247, 242, 0.90);
    border-left-color: rgba(255, 255, 255, 0.5);
}

.dc-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    flex-shrink: 0;
}
.dc-header-title { font-size: 18px; color: var(--panel-accent); display: flex; align-items: center; }
.dc-header-text { flex: 1; font-size: 15px; font-weight: 700; color: var(--bs-body-color); }

.dc-back-btn, .dc-close-btn {
    width: 32px; height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: var(--bs-gray-500);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
    font-size: 16px;
}
.dc-back-btn:hover, .dc-close-btn:hover {
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    color: var(--bs-body-color);
}

.dc-modes {
    flex: 1;
    position: relative;
    overflow: hidden;
}

.dc-mode {
    position: absolute;
    inset: 0;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: inherit;
}

.dc-slide-forward-enter-active,
.dc-slide-forward-leave-active,
.dc-slide-backward-enter-active,
.dc-slide-backward-leave-active {
    transition: transform 0.30s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.22s ease;
    will-change: transform, opacity;
}

.dc-slide-forward-enter-from { transform: translateX(100%); }
.dc-slide-forward-leave-to { transform: translateX(-30%); opacity: 0; }
.dc-slide-backward-enter-from { transform: translateX(-30%); opacity: 0; }
.dc-slide-backward-leave-to { transform: translateX(100%); }

.dc-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dc-section-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 0 4px; margin-bottom: 4px;
}
.dc-section-title {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; color: var(--bs-gray-500);
    margin: 0; padding: 0 4px;
}
.dc-section-actions { display: flex; gap: 6px; }
.dc-mini-btn {
    background: transparent; border: none; padding: 4px 8px;
    font-size: 11px; font-weight: 600; color: var(--panel-accent);
    border-radius: 6px; cursor: pointer;
    transition: background 0.15s;
}
.dc-mini-btn:hover:not(:disabled) {
    background: rgba(var(--panel-accent-rgb), 0.12);
}
.dc-mini-btn:disabled { color: var(--bs-gray-400); cursor: default; }

.dc-instance-list {
    list-style: none; margin: 0; padding: 0;
    display: flex; flex-direction: column; gap: 4px;
}

.dc-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    transition: background 0.15s, opacity 0.15s, transform 0.15s;
    user-select: none;
    position: relative;
}
.dc-row--clickable { cursor: pointer; }
.dc-row--clickable:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06); }
.dc-row--off { opacity: 0.5; }

.dc-row--dragging {
    opacity: 0.35;
    background: rgba(var(--panel-accent-rgb), 0.08);
}
.dc-row--drag-over::before {
    content: '';
    position: absolute;
    left: 8px; right: 8px;
    top: -2px;
    height: 2px;
    background: var(--panel-accent);
    border-radius: 2px;
    pointer-events: none;
}

.dc-row-grip {
    width: 18px; height: 24px;
    display: flex; align-items: center; justify-content: center;
    color: var(--bs-gray-400);
    flex-shrink: 0;
    cursor: grab;
    margin-left: -4px;
    touch-action: none;
}
.dc-row-grip:active { cursor: grabbing; }
.dc-row-grip .bi { font-size: 14px; }
.dc-row:hover .dc-row-grip { color: var(--bs-gray-600); }

.dc-row-icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0;
}

.dc-row-info {
    flex: 1; display: flex; flex-direction: column; gap: 2px;
    min-width: 0;
}
.dc-row-title {
    font-size: 13px; font-weight: 600; color: var(--bs-body-color);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dc-row-sub {
    font-size: 11px; color: var(--bs-gray-500);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.dc-icon-btn {
    width: 30px; height: 30px;
    border-radius: 6px; border: none;
    background: transparent;
    color: var(--bs-gray-500);
    cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
    font-size: 14px;
}
.dc-icon-btn:hover {
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    color: var(--bs-body-color);
}
.dc-icon-btn--danger:hover {
    background: rgba(var(--bs-danger-rgb, 241, 65, 108), 0.12);
    color: var(--bs-danger);
}

.dc-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 6px; padding: 24px; color: var(--bs-gray-500); font-size: 12px;
}
.dc-empty .bi { font-size: 32px; opacity: 0.4; }

.dc-add-btn {
    width: 100%;
    display: flex; align-items: center; justify-content: center;
    gap: 8px;
    padding: 10px 14px;
    border: none;
    background: var(--panel-accent);
    color: #fff;
    border-radius: 10px;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.1s;
}
.dc-add-btn:hover { opacity: 0.85; }
.dc-add-btn:active { transform: scale(0.98); }
.dc-add-btn .bi { font-size: 14px; }

.dc-template-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}
.dc-template-card {
    position: relative;
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    padding: 16px 10px;
    border-radius: 12px;
    background: var(--bs-body-bg);
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.15);
    cursor: pointer;
    transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.dc-template-card:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: var(--panel-accent);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.dc-template-card:disabled {
    opacity: 0.4;
    cursor: default;
}
html.dark .dc-template-card,
html.midnight .dc-template-card { background: rgba(255, 255, 255, 0.05); }

.dc-template-icon {
    width: 44px; height: 44px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
}
.dc-template-icon .bi { font-size: 20px; line-height: 1; }
.dc-template-name {
    font-size: 12px; font-weight: 600;
    color: var(--bs-body-color);
    text-align: center;
    line-height: 1.3;
}
.dc-template-dot {
    position: absolute; top: 8px; right: 8px;
    width: 8px; height: 8px;
    border-radius: 50%;
    box-shadow: 0 0 0 2px var(--bs-body-bg);
}
.dc-template-dot--multi {
    background: var(--panel-accent);
}
.dc-template-dot--added {
    background: var(--bs-success);
}
html.dark .dc-template-dot,
html.midnight .dc-template-dot {
    box-shadow: 0 0 0 2px rgba(20, 22, 28, 0.95);
}
.dc-template-badge--multi {
    background: rgba(var(--panel-accent-rgb), 0.16);
    color: var(--panel-accent);
}
.dc-template-badge--added {
    background: rgba(var(--bs-success-rgb, 80, 205, 137), 0.18);
    color: var(--bs-success);
}

.dc-form { display: flex; flex-direction: column; gap: 16px; }
.dc-form-header {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 4px;
}
.dc-form-icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
}
.dc-form-name {
    font-size: 14px; font-weight: 700; color: var(--bs-body-color);
}
.dc-form-empty {
    padding: 16px;
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06);
    border-radius: 8px;
    font-size: 12px; color: var(--bs-gray-500);
    text-align: center;
}
.dc-form-field {
    display: flex; flex-direction: column; gap: 6px;
}
.dc-form-label {
    font-size: 11px; font-weight: 600; color: var(--bs-gray-600);
    text-transform: uppercase; letter-spacing: 0.3px;
}
.dc-form-input { width: 100%; }
.dc-form-text {
    width: 100%;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.25);
    background: var(--bs-body-bg);
    color: var(--bs-body-color);
    font-size: 13px;
}
.dc-form-text:focus {
    outline: none;
    border-color: var(--panel-accent);
}
.dc-form-hint {
    font-size: 10.5px; color: var(--bs-gray-500);
    margin-top: 4px;
}

.dc-form-selected {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(var(--panel-accent-rgb), 0.10);
    border: 1px solid rgba(var(--panel-accent-rgb), 0.20);
    color: var(--bs-body-color);
    font-size: 13px; font-weight: 500;
}
.dc-form-selected .bi {
    color: var(--panel-accent);
    font-size: 15px;
}
.dc-form-selected > span { flex: 1; }
.dc-form-selected-clear {
    background: transparent; border: none;
    font-size: 11px; font-weight: 600; color: var(--panel-accent);
    cursor: pointer; padding: 4px 8px; border-radius: 6px;
    transition: background 0.15s;
}
.dc-form-selected-clear:hover {
    background: rgba(var(--panel-accent-rgb), 0.15);
}

.dc-weather-search { position: relative; }
.dc-weather-results {
    margin-top: 6px;
    background: var(--bs-body-bg);
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.20);
    border-radius: 8px;
    overflow: hidden;
    max-height: 240px;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
html.dark .dc-weather-results,
html.midnight .dc-weather-results {
    background: rgba(255, 255, 255, 0.04);
}
.dc-weather-result {
    width: 100%;
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    background: transparent; border: none;
    text-align: left;
    color: var(--bs-body-color);
    font-size: 13px;
    cursor: pointer;
    transition: background 0.12s;
}
.dc-weather-result:hover {
    background: rgba(var(--panel-accent-rgb), 0.10);
}
.dc-weather-result .bi {
    color: var(--bs-gray-500);
    font-size: 14px;
    flex-shrink: 0;
}
.dc-weather-empty {
    margin-top: 6px;
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: var(--bs-gray-500);
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06);
    border-radius: 8px;
}

.dc-submit-btn {
    width: 100%;
    padding: 10px 16px;
    background: var(--panel-accent);
    color: #fff;
    border: none; border-radius: 8px;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.1s;
    margin-top: 8px;
}
.dc-submit-btn:hover { opacity: 0.85; }
.dc-submit-btn:active { transform: scale(0.98); }

.dc-toggle {
    width: 38px; height: 22px;
    border-radius: 11px;
    background: var(--bs-gray-300, #e5e5ea);
    position: relative; flex-shrink: 0;
    transition: background 0.2s;
}
html.dark .dc-toggle:not(.dc-toggle--on),
html.midnight .dc-toggle:not(.dc-toggle--on) {
    background: rgba(255, 255, 255, 0.15);
}
.dc-toggle-thumb {
    position: absolute; top: 2px; left: 2px;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    transition: transform 0.2s;
}
.dc-toggle.dc-toggle--on { background: var(--panel-accent); }
.dc-toggle--on .dc-toggle-thumb { transform: translateX(16px); }

.dc-footer {
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.dc-reset-btn {
    width: 100%;
    display: flex; align-items: center; justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 10px;
    background: transparent;
    border: 1px solid rgba(var(--bs-danger-rgb, 241, 65, 108), 0.4);
    color: var(--bs-danger);
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}
.dc-reset-btn:hover {
    background: rgba(var(--bs-danger-rgb, 241, 65, 108), 0.08);
    border-color: var(--bs-danger);
}

.dc-drawer-enter-active,
.dc-drawer-leave-active { transition: opacity 0.22s ease; }
.dc-drawer-enter-active .dc-drawer,
.dc-drawer-leave-active .dc-drawer {
    transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}
.dc-drawer-enter-from,
.dc-drawer-leave-to { opacity: 0; }
.dc-drawer-enter-from .dc-drawer,
.dc-drawer-leave-to .dc-drawer { transform: translateX(100%); }
</style>
